'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useSidebarStore } from '@/shared/stores/sidebar-store';

interface DesktopShellProps {
  sidebar: ReactNode;
  children: ReactNode;
}

const SIDEBAR_WIDTH_EXPANDED = 213;

/**
 * Shared desktop shell used by Dashboard, Project, History and Account layouts.
 * Provides the outer container, max-width frame and the fixed-width left
 * sidebar column. Caller passes the sidebar content and the right-hand columns
 * (main content, optional middle panel, secondary sidebars) as `children`.
 *
 * Collapse behavior mirrors the Apple app (not Android): collapsing hides the
 * sidebar entirely and navigation moves to the floating bottom bar
 * (`MobileNavigation`), rather than shrinking into a compact icon rail.
 */
export function DesktopShell({ sidebar, children }: DesktopShellProps) {
  const collapsed = useSidebarStore((s) => s.collapsed);

  return (
    // Publish the content column's left offset (sidebar width + gap) as a CSS
    // var so viewport-fixed descendants (the floating Generate capsule) can
    // center over the content column instead of the whole viewport. 0 when the
    // sidebar is collapsed (content is full-width). The stacked floating footer
    // only exists below xl (< 1728px), so the max-w container adds no side
    // margin there and this px offset is exact.
    <div
      className="flex-1 min-h-0 w-full overflow-hidden bg-muted/30"
      style={
        {
          '--content-inset-left': collapsed
            ? '0px'
            : `${SIDEBAR_WIDTH_EXPANDED + 16}px`,
        } as CSSProperties
      }
    >
      <div className="h-full w-full">
        {/* No padding on this row: the right-hand content column must reach the
            window's top/right/bottom edges so its scrollbar pins to the window
            edge and scrolled content clips at the window, not at an inset box.
            The sidebar keeps its floating-card look via its own `my-4 ml-4`
            margins; `gap-4` still separates columns (incl. account's 3-pane). */}
        <div className="flex h-full gap-4">
          {!collapsed && (
            <aside
              className="flex-shrink-0 bg-muted/60 rounded-2xl border border-background shadow-[0_2px_12px_0_rgb(0_0_0_/_0.08)] my-4 ml-4 overflow-hidden flex flex-col"
              style={{ width: SIDEBAR_WIDTH_EXPANDED }}
            >
              {sidebar}
            </aside>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
